"use client";
import LoadingPage from "@/components/LoadingPage/LoadingPage";
import { GalleryI, PhotoI } from "@/interfaces/galleries/Galleries";
import {
  fetchGalleryByName,
  registerGallerySession,
} from "@/services/GalleriesService";
import { groupBy } from "@/utils/GalleryFunctions";
import { notifyError } from "@/utils/NotificationFunctions";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useUserContext } from "./UserContext";
import { createLog } from "@/services/LogService";
import GalleryPopUp from "@/components/GalleryPopUp/GalleryPopUp";
import { LogI } from "@/interfaces/Logs";
import { nameSlug } from "@/utils/Functions/NameSlug";
import GalleryPopUpQuiz from "@/components/GalleryPopUpQuiz/GalleryPopUpQuiz";
import { updateUserRecommendation } from "@/services/EditProfileService";
import { useRouter, useSearchParams } from "next/navigation";

interface GalleryContextI {
  gallery: GalleryI | null;
  photos: { [key: string]: PhotoI[] };
  years: Array<string>;
  activeXIndex: number;
  activeYIndex: number;
  setActiveXIndex: (index: number) => void;
  setActiveYIndex: (index: number) => void;
  getGallery: () => void;
  galleryLoader: boolean;
  setIsQuizAnswered: (isAnswered: any) => void;
}

export const GalleryContext = createContext<GalleryContextI>({
  gallery: null,
  photos: {},
  years: [],
  activeXIndex: 0,
  activeYIndex: 0,
  setActiveXIndex: () => {},
  setActiveYIndex: () => {},
  galleryLoader: false,
  getGallery: () => {},
  setIsQuizAnswered: () => {},
});

export function useGalleryContext() {
  return useContext(GalleryContext);
}

const GalleryContextProvider = ({
  galleryName,
  children,
}: {
  galleryName: string;
  children: ReactNode;
}) => {
  const [gallery, setGallery] = useState<any>(null);
  const [photos, setPhotos] = useState<{ [key: string]: PhotoI[] }>({});
  const queryParams = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [years, setYears] = useState<Array<string>>([]);
  const [activeXIndex, setActiveXIndex] = useState<number>(0);
  const [activeYIndex, setActiveYIndex] = useState<number>(0);
  const name = galleryName;
  const { currentUser, userToken, fetchUserData, userLoader } =
    useUserContext();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalQuiz, setShowModalQuiz] = useState<boolean>(false);
  const router = useRouter();
  const [isQuizAnswered, setIsQuizAnswered] = useState<boolean>(false);
  const getGallery = () => {
    setGallery(null);
    fetchGalleryByName({ name })
      .then((res) => {
        setGallery(res.data);
        if (res.data === null) {
          notifyError(
            "It appears that the gallery you are trying to view does not exist."
          );
          router.push("/");
        }
      })
      .catch((error) => {
        console.error(error);
        notifyError(
          "It appears that the gallery you are trying to view does not exist."
        );
        router.push("/");
      });
  };

  const updateRecommendationQuizConunt = async () => {
    try {
      const user = {
        userId: currentUser?._id,
        data: {
          recommendationQuizCount: currentUser?.recommendationQuizCount
            ? currentUser?.recommendationQuizCount + 1
            : 1,
          ...(currentUser?.recommendationQuizStatus === undefined && {
            recommendationQuizStatus: false,
          }),
        },
      };
      await updateUserRecommendation(user, userToken);
      fetchUserData();
    } catch (error) {
      console.log(error);
    }
  };
  const getPhotosArray = () => {
    const sortedPhotos = [...gallery.photoArray]
      .filter((element) => element.ifVerify === 1)
      .sort((a, b) => {
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        if (a.month !== b.month) {
          return a.month - b.month;
        }
        return a.day - b.day;
      });

    const fixedPhotos = sortedPhotos.filter((photo) => photo.year.length === 4);
    const temp = groupBy(fixedPhotos, "year");
    const tempYears = Object.keys(temp);

    setYears(tempYears);
    setPhotos(temp);
    setLoading(false);
  };
  useEffect(() => {
    // Verificar si hay un usuario autenticado
    if (!userLoader) {
      if (!currentUser && !isQuizAnswered) {
        console.log("user no log");

        const initModalCounter = localStorage.getItem("modalCounter");
        localStorage.getItem("isAnswered");
        let modalCounter = initModalCounter
          ? parseInt(initModalCounter, 10)
          : 0;

        // Incrementar el contador ya que la galería se ha abierto

        modalCounter += 1;
        localStorage.setItem("modalCounter", modalCounter.toString());

        // Verificar si mostrar el modal basado en el contador
        if (modalCounter === 2 || modalCounter % 7 === 0) {
          setShowModalQuiz(true);
        }
      }
    }
  }, [userLoader, currentUser]);
  useEffect(() => {
    if (!userLoader) {
      if (currentUser) {
        setIsQuizAnswered(currentUser.recommendationQuizStatus || false);
      } else {
        const saved = localStorage.getItem("isQuizAnswered");
        if (saved === null) {
          localStorage.setItem("isQuizAnswered", "false");
          setIsQuizAnswered(false);
        } else {
          setIsQuizAnswered(saved === "true");
        }
      }
    }
  }, [currentUser, userLoader]);
  useEffect(() => {
    getGallery();
  }, [name]);

  useEffect(() => {
    if (gallery) {
      getPhotosArray();
    }
  }, [gallery]);

  useEffect(() => {
    if (gallery && userToken) {
      const log: LogI = {
        type: "Gallery Open",
        typeNumber: 3,
        visitStart: Date.now(),
        gallery_name: gallery.name,
      };
      createLog(userToken, log);
    }
    if (
      currentUser &&
      (currentUser.recommendationQuizStatus === false ||
        currentUser.recommendationQuizStatus === undefined)
    ) {
      if (
        currentUser.recommendationQuizCount &&
        (currentUser.recommendationQuizCount === 2 ||
          currentUser.recommendationQuizCount % 7 === 0)
      ) {
        setShowModalQuiz(true);
      }
      updateRecommendationQuizConunt();
    }
  }, [gallery]);

  useEffect(() => {
    const photoId = queryParams.get("p");
    const year = queryParams.get("year");
    const v = queryParams.get("v");
    if (
      photoId &&
      year &&
      gallery &&
      Object.keys(photos).length &&
      photos[year]
    ) {
      const photoIndex = photos[year].findIndex((e: any) => e._id === photoId);
      router.push(`/${nameSlug(gallery.name)}/${year}/${photoIndex}?v=${v}`);
    }
  }, [
    gallery,
    photos,
    queryParams.get("year"),
    queryParams.get("p"),
    queryParams.get("v"),
  ]);

  useEffect(() => {
    if (!userLoader) {
      if (currentUser && !currentUser.notShowAgainGallery) {
        setShowModal(true);
      } else if (!currentUser) {
        const ShowPopUp =
          localStorage.getItem("notShowAgainGallery") === "true" ? false : true;
        setShowModal(ShowPopUp);
      }
    }
  }, [userLoader]);
  const values = {
    gallery,
    photos,
    years,
    activeXIndex,
    activeYIndex,
    setActiveXIndex,
    setActiveYIndex,
    getGallery,
    setIsQuizAnswered,
    galleryLoader: loading,
  };

  return (
    <GalleryContext.Provider value={values}>
      <GalleryPopUp show={showModal} onHide={() => setShowModal(false)} />
      <GalleryPopUpQuiz
        show={showModalQuiz}
        onHide={() => setShowModalQuiz(false)}
      />
      {loading && <LoadingPage />}
      {children}
    </GalleryContext.Provider>
  );
};

export default GalleryContextProvider;
